import { KeyboardEvent, Ref, useEffect, useImperativeHandle, useState } from 'react';
import { KeyValue } from '@mews/enums';
import { Modifiers, MonthOnlyCalendar } from '../Calendar';
import { noop } from '../utils/func';
import { CalendarService } from '../utils/services';

export interface BaseMonthPickerProps<D, M, Y> {
    date: D;
    getCalendarService: () => CalendarService<D, M, Y>;
    onDateClick: (event: React.MouseEvent, date: D) => void;
    onDateChange: (date: D) => void;
    monthDateNormalize: (month: M) => D;
    minDate?: D;
    maxDate?: D;
    impRef: Ref<{ handleKeyUp: (e: KeyboardEvent) => void }>;
    modifiers?: Modifiers<D>;
}

type operation<M> = (date: M) => M;

export const BaseMonthPicker = <D, M, Y>({
    onDateClick = noop,
    onDateChange = noop,
    getCalendarService,
    monthDateNormalize,
    date,
    impRef,
    ...other
}: BaseMonthPickerProps<D, M, Y>) => {
    const initialDate = date || getCalendarService().getCurrentDate();
    const normalizeFunction = monthDateNormalize ?? getCalendarService().getMonthFirstDate;
    const [selectedDate, setSelectedDate] = useState(initialDate);

    useEffect(() => {
        if (date && !getCalendarService().isDateEqual(date, selectedDate)) {
            setSelectedDate(date);
        }
    }, [date, getCalendarService, selectedDate]);

    const handleMonthClick = (e: React.MouseEvent, clickedMonth: M) => {
        const clickedDate = normalizeFunction(clickedMonth);
        setSelectedDate(clickedDate);
        onDateChange(clickedDate);
        onDateClick(e, clickedDate);
    };

    useImperativeHandle(impRef, () => ({
        handleKeyUp,
    }));

    const month = getCalendarService().getMonthOfDate(selectedDate);

    const moveDate = (e: React.MouseEvent | KeyboardEvent, operation: operation<M>) => {
        const newMonth = operation(month);
        const newDate = normalizeFunction(newMonth);
        setSelectedDate(newDate);
        onDateChange(newDate);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
        let handled;
        switch (e.key) {
            case KeyValue.ArrowUp:
                moveDate(e, getCalendarService().getNextMonth);
                handled = true;
                break;

            case KeyValue.ArrowDown:
                moveDate(e, getCalendarService().getPrevMonth);
                handled = true;
                break;

            default:
                break;
        }

        if (handled) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const calendarProps = {
        ...other,
        getCalendarService,
        month,
        onMonthClick: handleMonthClick,
    };

    return <MonthOnlyCalendar {...calendarProps} />;
};
